import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { api } from '../core/apiClient';

interface CreatedClient { id: string; name: string; cif: string; code?: string; }

interface Props {
  open: boolean;
  initialName?: string;
  onClose: () => void;
  onCreated: (client: CreatedClient) => void;
}

const COUNTRY_OPTIONS = [
  { value: 'CAT', label: 'Catalunya' },
  { value: 'ES',  label: 'Espanya' },
  { value: 'FR',  label: 'França' },
  { value: 'PT',  label: 'Portugal' },
];

const inputCls = 'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';
const labelCls = 'block text-xs font-medium text-muted-foreground mb-1';

// Modal mínim per crear un client sense sortir de la pantalla actual (ex:
// des de "Òrgan contractant" a Licitacions) — nomès els camps imprescindibles
// (Nom, CIF, Tipus, País); la resta (adreça, condicions, contactes...) es
// completen després editant la fitxa normal del client.
export default function CreateClientModal({ open, initialName, onClose, onCreated }: Props) {
  const [name, setName] = useState('');
  const [cif, setCif] = useState('');
  const [type, setType] = useState('ADMINISTRACION');
  const [country, setCountry] = useState('CAT');
  const [clientTypes, setClientTypes] = useState<{ id: number; code: string; name: string }[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(initialName ?? '');
    setCif('');
    setType('ADMINISTRACION');
    setCountry('CAT');
    setError(null);
    api.get<{ success: boolean; data: { id: number; code: string; name: string; isActive: number }[] }>('/catalog/client-types')
      .then((r) => setClientTypes((r.data ?? []).filter((t) => t.isActive)))
      .catch(() => setClientTypes([]));
  }, [open, initialName]);

  if (!open) return null;

  const handleSave = async () => {
    if (!name.trim()) { setError('El nom és obligatori'); return; }
    if (!cif.trim()) { setError('El CIF/NIF és obligatori'); return; }
    setSaving(true);
    setError(null);
    try {
      const res = await api.post<{ success: boolean; data: CreatedClient }>('/clients', {
        name: name.trim(),
        cif: cif.trim(),
        type,
        country,
      });
      onCreated(res.data);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Error creant el client');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => !saving && onClose()}>
      <div className="bg-white rounded-xl shadow-xl w-[420px]" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-semibold text-foreground">Nou client</h2>
          <button onClick={onClose} disabled={saving} className="text-muted-foreground hover:text-foreground disabled:opacity-40">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          {error && <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>}
          <div>
            <label className={labelCls}>Nom / Raó Social *</label>
            <input type="text" className={inputCls} value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          </div>
          <div>
            <label className={labelCls}>CIF/NIF *</label>
            <input type="text" className={inputCls} value={cif} onChange={(e) => setCif(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Tipus</label>
              <select className={`${inputCls} bg-white`} value={type} onChange={(e) => setType(e.target.value)}>
                {clientTypes.length > 0
                  ? clientTypes.map((t) => <option key={t.code} value={t.code}>{t.name}</option>)
                  : (
                    <>
                      <option value="ADMINISTRACION">Administració</option>
                      <option value="EMPRESA">Empresa</option>
                      <option value="AUTONOMO">Autònom</option>
                      <option value="PARTICULAR">Particular</option>
                    </>
                  )}
              </select>
            </div>
            <div>
              <label className={labelCls}>País</label>
              <select className={`${inputCls} bg-white`} value={country} onChange={(e) => setCountry(e.target.value)}>
                {COUNTRY_OPTIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground">La resta de dades (adreça, condicions, contactes...) es poden completar després editant la fitxa del client.</p>
        </div>
        <div className="p-4 border-t border-gray-100 flex gap-2 justify-end">
          <button onClick={onClose} disabled={saving} className="px-3 py-2 text-xs text-muted-foreground hover:bg-gray-100 rounded disabled:opacity-40">
            Cancel·lar
          </button>
          <button onClick={handleSave} disabled={saving}
            className="px-4 py-2 bg-gray-900 text-white rounded-lg text-xs font-medium hover:bg-gray-700 disabled:opacity-50">
            {saving ? 'Creant...' : 'Crear client'}
          </button>
        </div>
      </div>
    </div>
  );
}
